import { useState, useEffect, useRef } from "react";
import toast from "react-hot-toast";
import { motion } from "framer-motion";

const API_URL = "https://backend.aktcorp.com/relateddocuments/addrelateddocuments"; // ✅ New URL

const AddDocument = ({ onClose }) => {
    const [title, setTitle] = useState("");
    const [category, setCategory] = useState("");
    const [fileType, setFileType] = useState("");
    const [language, setLanguage] = useState("");
    const [file, setFile] = useState(null);
    const [loading, setLoading] = useState(false);
    const [success, setSuccess] = useState(false);
    const [errors, setErrors] = useState({});
    const [showPage, setShowPage] = useState(false);

    const fileInputRef = useRef(null);
    const jwtToken = localStorage.getItem("authToken"); // ✅ Fetch JWT token

    useEffect(() => {
        const timeout = setTimeout(() => setShowPage(true), 2000);
        return () => clearTimeout(timeout);
    }, []);

    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const timeout = setTimeout(() => setErrors({}), 2000);
            return () => clearTimeout(timeout);
        }
    }, [errors]);

    useEffect(() => {
        if (success) {
            const timeout = setTimeout(() => setSuccess(false), 3000);
            return () => clearTimeout(timeout);
        }
    }, [success]);

    const handleFileChange = (e) => {
        const selectedFile = e.target.files[0];
        if (selectedFile) {
            setFile(selectedFile);
            setErrors((prev) => ({ ...prev, file: false }));
        }
    };

    const handleSubmit = async () => {
        const newErrors = {
            title: !title,
            category: !category,
            fileType: !fileType,
            language: !language,
            file: !file,
        };

        if (Object.values(newErrors).some(Boolean)) {
            setErrors(newErrors);
            return;
        }

        setLoading(true);

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onloadend = async () => {
            try {
                const fileContent = reader.result.split(",")[1];

                const response = await fetch(API_URL, {
                    method: "POST",
                    headers: {
                        Authorization: `Bearer ${jwtToken}`, // ✅ Use JWT token
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify({
                        title,
                        category,
                        fileType,
                        language,
                        fileContent,
                    }),
                });

                if (!response.ok) throw new Error("Upload failed");

                setTimeout(() => {
                    setLoading(false);
                    setSuccess(true);
                    setTitle("");
                    setCategory("");
                    setFileType("");
                    setLanguage("");
                    setFile(null);
                    if (fileInputRef.current) fileInputRef.current.value = "";
                }, 2000);
            } catch (error) {
                setLoading(false);
                toast.error(error.message, { duration: 3000 });
            }
        };
    };

    if (!showPage) {
        return (
            <motion.div
                animate={{ rotate: 360 }}
                transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-24 h-24 border-4 border-t-transparent border-blue-600 rounded-full mx-auto mt-32"
            ></motion.div>
        );
    }

    return (
        <div className="p-6 max-w-md mx-auto bg-white rounded-xl shadow-md text-center mb-8 mt-6">
            <motion.h2
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="text-3xl font-bold text-gray-900 bg-clip-text text-center mb-4 text-transparent bg-gradient-to-r from-blue-500 to-purple-500"
            >
                Add New Document
            </motion.h2>

            <div className="space-y-4 text-left">
                <label className="text-gray-600 font-medium">PDF Name:</label>
                <input
                    type="text"
                    placeholder="Enter PDF Name"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    className={`w-full p-2 border rounded-lg mt-1 ${errors.title ? "border-red-500" : ""}`}
                />
                {errors.title && <p className="text-red-500 text-sm">*Title is required</p>}

                <label className="text-gray-600 font-medium">Category:</label>
                <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className={`w-full p-2 border rounded-lg mt-1 ${errors.category ? "border-red-500" : ""}`}
                >
                    <option value="">Select Category</option>
                    <option value="Technical Data Sheet">Technical Data Sheet</option>
                    <option value="Product Certifications">Product Certifications</option>
                    <option value="Sell Sheets & Brochures">Sell Sheets & Brochures</option>
                    <option value="Submittal Forms">Submittal Forms</option>
                    <option value="Other">Other</option>
                </select>
                {errors.category && <p className="text-red-500 text-sm">*Category is required</p>}

                <label className="text-gray-600 font-medium">File Type:</label>
                <input
                    type="text"
                    placeholder="PDF"
                    value={fileType}
                    onChange={(e) => setFileType(e.target.value)}
                    className={`w-full p-2 border rounded-lg mt-1 ${errors.fileType ? "border-red-500" : ""}`}
                />
                {errors.fileType && <p className="text-red-500 text-sm">*File type is required</p>}

                <label className="text-gray-600 font-medium">Language:</label>
                <select
                    value={language}
                    onChange={(e) => setLanguage(e.target.value)}
                    className={`w-full p-2 border rounded-lg mt-1 ${errors.language ? "border-red-500" : ""}`}
                >
                    <option value="">Select Language</option>
                    <option value="English">English</option>
                    <option value="Simplified Chinese">Simplified Chinese</option>
                    <option value="Spanish">Spanish</option>
                    <option value="Portuguese">Portuguese</option>
                    <option value="French">French</option>
                </select>
                {errors.language && <p className="text-red-500 text-sm">*Language is required</p>}

                <label className="text-gray-600 font-medium">Upload PDF:</label>
                <input
                    ref={fileInputRef}
                    type="file"
                    accept="application/pdf"
                    onChange={handleFileChange}
                    className="w-full p-2 border rounded-lg bg-gray-100 mt-1"
                />
                {errors.file && <p className="text-red-500 text-sm">*PDF file is required</p>}

                {loading ? (
                    <div className="flex flex-col items-center justify-center space-y-4">
                        <div className="animate-spin h-12 w-12 border-4 border-blue-500 border-t-transparent rounded-full"></div>
                        <p className="text-lg font-medium text-blue-500">Uploading...</p>
                    </div>
                ) : (
                    <button
                        onClick={handleSubmit}
                        className="w-full bg-blue-500 text-white p-3 rounded-lg hover:bg-blue-600 transition"
                    >
                        Upload
                    </button>
                )}
            </div>

            {success && (
                <p className="mt-4 text-green-600 font-medium">Document uploaded successfully!</p>
            )}
        </div>
    );
};

export default AddDocument;
